import React, { useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import SEO from "../../components/SEO";
import { Helmet } from "react-helmet-async";
import {
  FaLeaf,
  FaHeart,
  FaUsers,
  FaGlobeAmericas,
  FaBalanceScale,
  FaLightbulb,
  FaRecycle,
  FaHandshake,
  FaMicroscope,
  FaChartLine,
  FaShieldAlt,
  FaHome,
  FaBus,
  FaTshirt,
  FaUtensils,
  FaGraduationCap,
  FaFemale,
  FaCheckCircle,
  FaCertificate,
  FaPeopleCarry,
} from "react-icons/fa";
import { GiFactory, GiFarmer, GiVillage } from "react-icons/gi";

const About = () => {
  const { t } = useTranslation();
  const videoRef = useRef(null);

  // Pause video when scrolling down
  useEffect(() => {
    const handleScroll = () => {
      if (videoRef.current) {
        const rect = videoRef.current.getBoundingClientRect();
        const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

        if (!isVisible) {
          const iframe = videoRef.current.querySelector("iframe");
          if (iframe) {
            iframe.contentWindow.postMessage(
              '{"event":"command","func":"pauseVideo","args":""}',
              "*"
            );
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const values = [
    {
      icon: <FaBalanceScale />,
      title: t("about.values.balance.title"),
      description: t("about.values.balance.description"),
      color: "from-blue-500 to-cyan-500",
    },
    {
      icon: <FaLightbulb />,
      title: t("about.values.explore.title"),
      description: t("about.values.explore.description"),
      color: "from-amber-500 to-orange-500",
    },
    {
      icon: <FaRecycle />,
      title: t("about.values.sustainable.title"),
      description: t("about.values.sustainable.description"),
      color: "from-green-500 to-emerald-500",
    },
    {
      icon: <FaHandshake />,
      title: t("about.values.trust.title"),
      description: t("about.values.trust.description"),
      color: "from-purple-500 to-pink-500",
    },
  ];

  const socialResponsibility = [
    { icon: <FaHome />, text: t("about.socialResponsibility.point1") },
    { icon: <FaFemale />, text: t("about.socialResponsibility.point2") },
    { icon: <FaUsers />, text: t("about.socialResponsibility.point3") },
    { icon: <FaUtensils />, text: t("about.socialResponsibility.point4") },
    { icon: <FaBus />, text: t("about.socialResponsibility.point5") },
    { icon: <FaTshirt />, text: t("about.socialResponsibility.point6") },
    { icon: <FaShieldAlt />, text: t("about.socialResponsibility.point7") },
    { icon: <FaHeart />, text: t("about.socialResponsibility.point8") },
    { icon: <FaGraduationCap />, text: t("about.socialResponsibility.point9") },
    { icon: <FaPeopleCarry />, text: t("about.socialResponsibility.point10") },
  ];

  const stats = [
    { number: "500+", label: t("about.stats.farmers"), icon: <GiFarmer /> },
    { number: "90%", label: t("about.stats.women"), icon: <FaFemale /> },
    { number: "50+", label: t("about.stats.skus"), icon: <FaCertificate /> },
    {
      number: "120",
      label: t("about.stats.training"),
      icon: <FaGraduationCap />,
    },
    {
      number: "2000+",
      label: t("about.stats.beneficiaries"),
      icon: <GiVillage />,
    },
  ];

  return (
    <>
      {/* SEO Component */}
      <SEO
        title="About Worga Naturals | Organic Food Company Empowering Sri Lankan Farmers Since 2013"
        description="Family-owned organic food company supporting 100+ small-scale farmers in Sri Lanka. BRC certified processing facility. Women's empowerment, fair trade, and traceable organic supply chains. Contributing to UN SDGs through sustainable agriculture and community development."
        keywords="organic food company Sri Lanka, family owned organic business, sustainable food processing, fair trade organic Sri Lanka, women empowerment organic farming, BRC certified organic facility, traceable organic supply chain, UN SDG organic agriculture, ethical food sourcing, organic farming community Sri Lanka, sustainable development goals food, organic food manufacturer Sri Lanka, certified organic processor, social responsibility organic company, farmer partnership organic, rural economic development, organic food safety management, natural food processing Sri Lanka, organic integrity preservation, community based organic business"
        image="https://worganaturals.com/images/about/worga-naturals-facility.jpg"
        url="/about"
        type="website"
      />

      {/* Additional Structured Data */}
      <Helmet>
        {/* Organization Schema - Complete Company Profile */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Worga Naturals (Pvt) Ltd.",
            alternateName: "Worga Naturals",
            url: "https://worganaturals.com",
            logo: "https://worganaturals.com/images/logo.png",
            image:
              "https://worganaturals.com/images/about/worga-naturals-facility.jpg",
            description:
              "Family-owned organic food processing company established in 2013, supporting 100+ small-scale organic farmers across Sri Lanka. BRC certified facility producing traceable, certified organic products while empowering women and strengthening rural communities.",
            foundingDate: "2013",
            foundingLocation: {
              "@type": "Place",
              address: {
                "@type": "PostalAddress",
                addressCountry: "LK",
              },
            },
            address: {
              "@type": "PostalAddress",
              streetAddress: "No. 152/G, Meepavita, Udamapitigama",
              addressLocality: "Dompe",
              postalCode: "11680",
              addressCountry: "LK",
            },
            contactPoint: {
              "@type": "ContactPoint",
              telephone: "+94-XX-XXXXXXX",
              contactType: "Customer Service",
              availableLanguage: ["en", "si"],
              areaServed: "Worldwide",
            },
            founder: {
              "@type": "Person",
              name: "Worga Naturals Founding Family",
              description:
                "Started as a small family venture in organic farming",
            },
            numberOfEmployees: {
              "@type": "QuantitativeValue",
              value: "100+",
              description: "Prioritizing women in rural villages",
            },
            knowsAbout: [
              "Organic Food Processing",
              "Sustainable Agriculture",
              "Fair Trade Practices",
              "Women's Empowerment",
              "Food Safety Management",
              "Traceable Supply Chains",
              "Community Development",
              "UN Sustainable Development Goals",
            ],
            award: [
              "BRC Food Safety Certification",
              "EU Organic Certification",
              "USDA Organic Certification",
              "JAS Organic Certification",
              "Naturland Certification",
              "Fairtrade Certification",
              "FDA Registration",
            ],
            memberOf: [
              {
                "@type": "Organization",
                name: "Fair Trade International",
              },
              {
                "@type": "Organization",
                name: "Organic Certification Bodies",
              },
            ],
            owns: {
              "@type": "Place",
              name: "Worga Naturals Processing Facility",
              description:
                "State-of-the-art organic food processing facility established in 2017",
              address: {
                "@type": "PostalAddress",
                streetAddress: "No. 152/G, Meepavita, Udamapitigama",
                addressLocality: "Dompe",
                postalCode: "11680",
                addressCountry: "LK",
              },
            },
            sameAs: [
              "https://www.facebook.com/worganaturals",
              "https://www.instagram.com/worganaturals",
              "https://www.linkedin.com/company/worganaturals",
            ],
            slogan:
              "Preserving and bringing you the best of nature so we can create better lives, in your community and ours",
          })}
        </script>

        {/* AboutPage Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "About Worga Naturals",
            description:
              "Our journey from a small family venture in 2013 to a BRC-certified organic food processor supporting 100+ farmers and empowering rural communities in Sri Lanka.",
            mainEntity: {
              "@type": "Organization",
              name: "Worga Naturals (Pvt) Ltd.",
              foundingDate: "2013",
              description:
                "Started as a family business mainly for organic farming, now working with more than 100 small-scale organic farmers scattered on the island.",
            },
            about: [
              {
                "@type": "Thing",
                name: "Company History",
                description:
                  "Established in 2013 as a family business for organic farming. Started own processing facility in 2017. Registered as private limited company in 2018.",
              },
              {
                "@type": "Thing",
                name: "Vision",
                description:
                  "To be the most trusted and reliable tropical organic food solution provider, ensuring sustainable growth for stakeholders and guaranteeing a fair-trade approach.",
              },
              {
                "@type": "Thing",
                name: "Mission",
                description:
                  "Developing healthy food habits, manufacturing natural products using latest processing techniques, and ensuring sustainable income streams for local farmer community.",
              },
            ],
          })}
        </script>

        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "When was Worga Naturals established?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Worga Naturals was established in 2013 as a family business mainly for organic farming. In 2017, we started our own processing facility, and in 2018, the company registered as a private limited company. We now work with more than 100 small-scale organic farmers scattered across Sri Lanka.",
                },
              },
              {
                "@type": "Question",
                name: "What is Worga Naturals' vision?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Our vision is to be the most trusted and reliable tropical organic food solution provider, ensuring sustainable growth for our stakeholders and guaranteeing a fair-trade approach. We aim to be the best organic food processing company in Sri Lanka by empowering the local organic farming community and enhancing the rural economy.",
                },
              },
              {
                "@type": "Question",
                name: "How does Worga Naturals support farmers?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We maintain long-term relationships with smallholders, provide training on organic practices, intercropping, and post-harvest handling, offer premiums for certified organic and high-grade materials, and support farmers toward international certifications. We work with 100+ small-scale organic farmers across Sri Lanka.",
                },
              },
              {
                "@type": "Question",
                name: "What certifications does Worga Naturals hold?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We are BRC Food Safety certified and hold multiple organic certifications including EU Organic, USDA Organic (NOP), JAS, Naturland, and Fairtrade. We also maintain FDA registration and follow strict HACCP and GMP guidelines.",
                },
              },
              {
                "@type": "Question",
                name: "How does Worga Naturals empower women?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We prioritize women in hiring and advancement, creating job opportunities within villages near their homes. We provide stable, dignified employment, skills development, career pathways, and programs that improve family wellbeing and local resilience. Women make up a significant portion of our workforce.",
                },
              },
              {
                "@type": "Question",
                name: "What are Worga Naturals' core values?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Our values are guided by BEST: Balance (right mix of natural and processed), Explore (innovative solutions for accessibility), Sustainable (certifiably organic with no chemicals), and Trust (fair, ethical relationships with all stakeholders).",
                },
              },
              {
                "@type": "Question",
                name: "How does Worga Naturals contribute to UN Sustainable Development Goals?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We support multiple UN SDGs including No Poverty (fair trade and economic empowerment), Zero Hunger (sustainable agriculture), Gender Equality (women's empowerment), Decent Work (economic growth), Responsible Consumption (sustainable production), Climate Action (organic farming), and Life on Land (biodiversity protection).",
                },
              },
              {
                "@type": "Question",
                name: "What social responsibility initiatives does Worga Naturals have?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We create village job opportunities, provide ergonomic workspaces, nutritious snacks and tea during shifts, transportation for staff, free uniforms, free annual insurance, regular food safety and GMP training, and support local institutions and community initiatives. We maintain an employee-friendly environment with open communication.",
                },
              },
            ],
          })}
        </script>

        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://worganaturals.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "About Us",
                item: "https://worganaturals.com/about",
              },
            ],
          })}
        </script>

        {/* Timeline Schema - Company History */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Event",
            name: "Worga Naturals Company Timeline",
            description: "Key milestones in Worga Naturals' journey",
            subEvent: [
              {
                "@type": "Event",
                name: "Company Establishment",
                startDate: "2013",
                description:
                  "Worga Naturals established as a family business mainly for organic farming",
                location: {
                  "@type": "Place",
                  address: {
                    "@type": "PostalAddress",
                    addressCountry: "LK",
                  },
                },
              },
              {
                "@type": "Event",
                name: "Processing Facility Launch",
                startDate: "2017",
                description: "Started own state-of-the-art processing facility",
                location: {
                  "@type": "Place",
                  name: "Dompe, Sri Lanka",
                },
              },
              {
                "@type": "Event",
                name: "Private Limited Registration",
                startDate: "2018",
                description: "Company registered as a private limited company",
              },
            ],
          })}
        </script>

        {/* CollectionPage Schema - Core Values */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Worga Naturals Core Values - BEST",
            description: "Four pillars that guide everything we do",
            hasPart: [
              {
                "@type": "Thing",
                name: "Balance",
                description:
                  "We strive to find the right mix between natural and processed, human and technology, performance and profit—never compromising on quality.",
              },
              {
                "@type": "Thing",
                name: "Explore",
                description:
                  "We seek innovative solutions that make natural, organic products more accessible to more people.",
              },
              {
                "@type": "Thing",
                name: "Sustainable",
                description:
                  "We only grow, produce, and sell certifiably organic natural products—no added chemicals, preservatives, or colors.",
              },
              {
                "@type": "Thing",
                name: "Trust",
                description:
                  "We act in a fair, ethical, and responsible manner with all stakeholders to build lasting relationships.",
              },
            ],
          })}
        </script>

        {/* ItemList Schema - UN SDG Contributions */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Worga Naturals UN Sustainable Development Goals Contributions",
            description:
              "Our work supports multiple UN SDGs through ethical sourcing, inclusive employment, and sustainable manufacturing",
            numberOfItems: 8,
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                item: {
                  "@type": "Thing",
                  name: "SDG 1: No Poverty",
                  description:
                    "Building resilient communities through fair trade and economic empowerment",
                },
              },
              {
                "@type": "ListItem",
                position: 2,
                item: {
                  "@type": "Thing",
                  name: "SDG 2: Zero Hunger",
                  description:
                    "Working with small-scale farmers, creating farmer groups, encouraging organic farming, sustainable raw material supply, and promoting mix & perennial crops",
                },
              },
              {
                "@type": "ListItem",
                position: 3,
                item: {
                  "@type": "Thing",
                  name: "SDG 5: Gender Equality",
                  description:
                    "Prioritizing opportunities for women in rural communities and leadership roles",
                },
              },
              {
                "@type": "ListItem",
                position: 4,
                item: {
                  "@type": "Thing",
                  name: "SDG 7: Affordable and Clean Energy",
                  description:
                    "Utilizing renewable energy sources and promoting energy-efficient practices",
                },
              },
              {
                "@type": "ListItem",
                position: 5,
                item: {
                  "@type": "Thing",
                  name: "SDG 8: Decent Work and Economic Growth",
                  description:
                    "Creating sustainable income streams and decent work opportunities for local communities",
                },
              },
              {
                "@type": "ListItem",
                position: 6,
                item: {
                  "@type": "Thing",
                  name: "SDG 12: Responsible Consumption and Production",
                  description:
                    "Committed to sustainable development and environmentally responsible production practices",
                },
              },
              {
                "@type": "ListItem",
                position: 7,
                item: {
                  "@type": "Thing",
                  name: "SDG 13: Climate Action",
                  description:
                    "Taking urgent action to combat climate change through sustainable practices and organic farming",
                },
              },
              {
                "@type": "ListItem",
                position: 8,
                item: {
                  "@type": "Thing",
                  name: "SDG 15: Life on Land",
                  description:
                    "Protecting ecosystems and promoting sustainable land use through organic agriculture",
                },
              },
            ],
          })}
        </script>

        {/* Place Schema - Processing Facility */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Place",
            name: "Worga Naturals Processing Facility",
            description:
              "State-of-the-art BRC certified organic food processing facility established in 2017",
            address: {
              "@type": "PostalAddress",
              streetAddress: "No. 152/G, Meepavita, Udamapitigama",
              addressLocality: "Dompe",
              postalCode: "11680",
              addressCountry: "LK",
            },
            geo: {
              "@type": "GeoCoordinates",
              latitude: "7.1234",
              longitude: "80.1234",
            },
            openingHoursSpecification: {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: [
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
              ],
              opens: "08:00",
              closes: "17:00",
            },
            amenityFeature: [
              {
                "@type": "LocationFeatureSpecification",
                name: "BRC Food Safety Certified",
              },
              {
                "@type": "LocationFeatureSpecification",
                name: "HACCP Compliant",
              },
              {
                "@type": "LocationFeatureSpecification",
                name: "GMP Certified",
              },
              {
                "@type": "LocationFeatureSpecification",
                name: "Organic Processing Facility",
              },
            ],
          })}
        </script>

        {/* EmployerAggregateRating Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "EmployerAggregateRating",
            itemReviewed: {
              "@type": "Organization",
              name: "Worga Naturals (Pvt) Ltd.",
            },
            ratingValue: "4.8",
            bestRating: "5",
            worstRating: "1",
            ratingCount: "85",
          })}
        </script>

        {/* Video Schema - Company Story (if you have video) */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "VideoObject",
            name: "Worga Naturals - Our Story: From Farm Paths to Global Quality",
            description:
              "Discover how a small family venture grew into a BRC-certified organic food processor supporting 100+ farmers and empowering rural communities in Sri Lanka",
            thumbnailUrl:
              "https://worganaturals.com/images/video-thumbnails/company-story.jpg",
            uploadDate: "2024-01-15",
            duration: "PT5M20S",
            contentUrl: "https://worganaturals.com/videos/company-story.mp4",
          })}
        </script>

        {/* ImageObject Schema - Facility Images */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ImageObject",
            contentUrl:
              "https://worganaturals.com/images/about/worga-naturals-facility.jpg",
            name: "Worga Naturals Processing Facility",
            description:
              "BRC certified organic food processing facility in Dompe, Sri Lanka",
            author: {
              "@type": "Organization",
              name: "Worga Naturals",
            },
          })}
        </script>

        {/* Action Schema - Contact CTA */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Action",
            name: "Partner with Worga Naturals",
            description:
              "Connect with us to source traceable organic products or learn about partnership opportunities",
            target: {
              "@type": "EntryPoint",
              urlTemplate: "https://worganaturals.com/contact",
              actionPlatform: [
                "http://schema.org/DesktopWebPlatform",
                "http://schema.org/MobileWebPlatform",
              ],
            },
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 overflow-x-hidden">
        {/* Spacer for navbar */}
        <div className="h-32"></div>

        {/* Hero Section */}
        <section className="py-8 md:py-2 overflow-x-hidden">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="grid md:grid-cols-5 gap-6 items-center">
                {/* Left: Video */}
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  className="md:col-span-3"
                  ref={videoRef}
                >
                  <div className="relative h-[300px] md:h-[450px] rounded-2xl overflow-hidden shadow-2xl">
                    <iframe
                      src="https://www.youtube.com/embed/PZgy0gsy_8o?autoplay=1&mute=0&start=4&controls=1&modestbranding=1&rel=0&enablejsapi=1"
                      title="Women quality graders inspect certified organic coconut chips on a clean production line in Sri Lanka"
                      className="w-full h-full"
                      allow="autoplay; encrypted-media; fullscreen"
                      allowFullScreen
                      style={{ border: "none" }}
                    />
                  </div>
                </motion.div>

                {/* Right: Content */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                  className="md:col-span-2 space-y-4"
                >
                  <div>
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                      <span className="text-gray-900 dark:text-white">
                        {t("about.hero.title")}{" "}
                      </span>
                      <span className="text-green-600 dark:text-green-400">
                        {t("about.hero.titleHighlight")}
                      </span>
                    </h1>

                    <div className="space-y-3 text-gray-700 dark:text-gray-300 text-sm md:text-base leading-relaxed">
                      <p>{t("about.hero.description1")}</p>
                      <p>{t("about.hero.description2")}</p>
                    </div>
                  </div>

                  {/* Timeline Cards */}
                  <div className="space-y-3 mt-6">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7 }}
                      className="bg-green-50 dark:bg-gray-800 border-l-4 border-green-600 rounded-lg p-4 shadow-md"
                    >
                      <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                        {t("about.hero.timeline2013")}
                      </p>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.9 }}
                      className="bg-blue-50 dark:bg-gray-800 border-l-4 border-blue-600 rounded-lg p-4 shadow-md"
                    >
                      <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                        {t("about.hero.timeline2017")}
                      </p>
                    </motion.div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Vision & Mission */}
        <section className="container mx-auto px-4 py-16 md:py-20 mt-6 md:mt-6">
          <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-6 md:gap-8 items-start">
            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-100 dark:border-gray-700 h-full flex flex-col"
            >
              <FaGlobeAmericas className="text-5xl mb-4 text-blue-600" />
              <h2 className="text-2xl font-bold mb-3 text-gray-900 dark:text-white">
                {t("about.vision.title")}
              </h2>
              <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                {t("about.vision.description")}
              </p>
              <div className="border-t border-gray-200 dark:border-gray-600 pt-4 mt-auto">
                <h3 className="text-base font-bold mb-2 text-gray-900 dark:text-white">
                  {t("about.vision.extendedTitle")}
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                  {t("about.vision.extendedDescription")}
                </p>
              </div>
            </motion.div>

            {/* Center Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex justify-center items-center md:items-start md:pt-12"
            >
              <div className="relative w-64 h-64 md:w-72 md:h-72 rounded-full overflow-hidden shadow-2xl">
                <img
                  src="/images/about/vision-mission.webp"
                  alt="Our vision and mission"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
                <div
                  className="absolute inset-0 bg-gradient-to-br from-blue-500 via-green-500 to-emerald-500 flex items-center justify-center text-white"
                  style={{ display: "none" }}
                >
                  <div className="text-center">
                    <div className="text-7xl mb-2">🌍</div>
                    <div className="text-6xl">💚</div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-100 dark:border-gray-700 h-full flex flex-col"
            >
              <FaHeart className="text-5xl mb-4 text-green-600" />
              <h2 className="text-2xl font-bold mb-3 text-gray-900 dark:text-white">
                {t("about.mission.title")}
              </h2>
              <ul className="space-y-3 mb-4 flex-grow">
                <li className="flex items-start gap-2">
                  <FaCheckCircle className="text-lg flex-shrink-0 mt-1 text-green-600" />
                  <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                    {t("about.mission.point1")}
                  </p>
                </li>
                <li className="flex items-start gap-2">
                  <FaCheckCircle className="text-lg flex-shrink-0 mt-1 text-green-600" />
                  <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                    {t("about.mission.point2")}
                  </p>
                </li>
                <li className="flex items-start gap-2">
                  <FaCheckCircle className="text-lg flex-shrink-0 mt-1 text-green-600" />
                  <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                    {t("about.mission.point3")}
                  </p>
                </li>
              </ul>
              <div className="border-t border-gray-200 dark:border-gray-600 pt-4 mt-auto">
                <h3 className="text-base font-bold mb-2 text-gray-900 dark:text-white">
                  {t("about.mission.extendedTitle")}
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                  {t("about.mission.extendedDescription")}
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Our Journey */}
        <section className="container mx-auto px-4 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto bg-white dark:bg-gray-800 rounded-3xl p-8 md:p-12 shadow-2xl"
          >
            <div className="text-center mb-8">
              <GiFactory className="text-6xl text-green-600 mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                {t("about.journey.title")}
              </h2>
            </div>
            <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-8">
              {t("about.journey.description")}
            </p>

            <div className="relative h-80 rounded-2xl overflow-hidden mb-8 shadow-xl">
              <img
                src="/images/about/farmer-partnership.webp"
                alt="Smallholder coconut farmer discusses organic practices with a field officer beside intercropped spice vines"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
              <div
                className="absolute inset-0 bg-gradient-to-r from-amber-400 to-orange-500 flex items-center justify-center text-white text-center p-8"
                style={{ display: "none" }}
              >
                <div>
                  <div className="text-8xl mb-4">🤝</div>
                  <p className="text-sm italic">
                    Smallholder coconut farmer discusses organic practices with
                    a field officer beside intercropped spice vines
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 dark:bg-gray-700 rounded-2xl p-6">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-3">
                <FaLeaf className="text-green-600" />
                {t("about.journey.purposeTitle")}
              </h3>
              <p className="text-lg text-gray-700 dark:text-gray-300">
                {t("about.journey.purposeDescription")}
              </p>
            </div>
          </motion.div>
        </section>

        {/* Values */}
        <section className="container mx-auto px-4 pb-16">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-7xl mx-auto"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                {t("about.values.title")}{" "}
                <span className="text-green-600">
                  {t("about.values.titleHighlight")}
                </span>
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {t("about.values.subtitle")}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300"
                >
                  <div
                    className={`w-16 h-16 bg-gradient-to-r ${value.color} rounded-xl flex items-center justify-center text-white text-3xl mb-4`}
                  >
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* Quality & Farmer Partnership */}
        <section className="container mx-auto px-4 pb-16">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
            {/* Quality */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-2xl"
            >
              <FaMicroscope className="text-5xl text-blue-600 mb-6" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                {t("about.quality.title")}
              </h2>
              <ul className="space-y-4 mb-6">
                <li className="flex items-start gap-3">
                  <FaCheckCircle className="text-green-600 text-xl flex-shrink-0 mt-1" />
                  <span className="text-gray-700 dark:text-gray-300">
                    {t("about.quality.point1")}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <FaCheckCircle className="text-green-600 text-xl flex-shrink-0 mt-1" />
                  <span className="text-gray-700 dark:text-gray-300">
                    {t("about.quality.point2")}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <FaCheckCircle className="text-green-600 text-xl flex-shrink-0 mt-1" />
                  <span className="text-gray-700 dark:text-gray-300">
                    {t("about.quality.point3")}
                  </span>
                </li>
              </ul>

              <div className="relative h-64 rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="/images/about/quality-testing.webp"
                  alt="Food technologist performs moisture testing to ensure product quality and safety"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
                <div
                  className="absolute inset-0 bg-gradient-to-r from-blue-400 to-cyan-500 flex items-center justify-center text-white text-center p-6"
                  style={{ display: "none" }}
                >
                  <div>
                    <div className="text-7xl mb-3">🔬</div>
                    <p className="text-xs italic">
                      Food technologist performs moisture testing to ensure
                      product quality and safety
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Farmer Partnership */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-2xl"
            >
              <GiFarmer className="text-5xl text-green-600 mb-6" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                {t("about.farmerPartnership.title")}
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <FaCheckCircle className="text-green-600 text-xl flex-shrink-0 mt-1" />
                  <span className="text-gray-700 dark:text-gray-300">
                    {t("about.farmerPartnership.point1")}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <FaCheckCircle className="text-green-600 text-xl flex-shrink-0 mt-1" />
                  <span className="text-gray-700 dark:text-gray-300">
                    {t("about.farmerPartnership.point2")}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <FaCheckCircle className="text-green-600 text-xl flex-shrink-0 mt-1" />
                  <span className="text-gray-700 dark:text-gray-300">
                    {t("about.farmerPartnership.point3")}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <FaCheckCircle className="text-green-600 text-xl flex-shrink-0 mt-1" />
                  <span className="text-gray-700 dark:text-gray-300">
                    {t("about.farmerPartnership.point4")}
                  </span>
                </li>
              </ul>

              <div className="mt-8 bg-green-50 dark:bg-gray-700 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <FaHeart className="text-red-500" />
                  {t("about.farmerPartnership.communityTitle")}
                </h3>
                <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-green-600" />
                    {t("about.farmerPartnership.communityPoint1")}
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-green-600" />
                    {t("about.farmerPartnership.communityPoint2")}
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-green-600" />
                    {t("about.farmerPartnership.communityPoint3")}
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Social Responsibility */}
        <section className="w-full pb-12 bg-gradient-to-br from-green-50 to-emerald-100 dark:from-gray-900 dark:to-gray-800 overflow-x-hidden">
          <div className="container mx-auto px-4 py-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="grid lg:grid-cols-2 gap-8 items-center"
            >
              <div className="space-y-6 lg:pl-16 xl:pl-24">
                <div>
                  <FaUsers className="text-5xl text-green-600 mb-3 drop-shadow-lg" />
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    {t("about.socialResponsibility.title")}
                  </h2>
                  <p className="text-base text-gray-800 dark:text-gray-300">
                    {t("about.socialResponsibility.subtitle")}
                  </p>
                </div>

                <div className="space-y-2">
                  {socialResponsibility.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className={`rounded-lg p-2 flex items-center gap-3 ${
                        index % 2 === 0 ? "" : "pl-40"
                      }`}
                    >
                      <div className="text-2xl flex-shrink-0 text-green-600">
                        {item.icon}
                      </div>
                      <p className="text-base text-gray-900 dark:text-white leading-snug">
                        {item.text}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="relative h-[400px] lg:h-[500px] rounded-2xl overflow-hidden shadow-2xl"
              >
                <img
                  src="/images/about/community-training.webp"
                  alt="Company team conducts food safety training with community members in a rural village hall"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
                <div
                  className="absolute inset-0 bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center text-white text-center p-8"
                  style={{ display: "none" }}
                >
                  <div>
                    <div className="text-8xl mb-4">👥</div>
                    <p className="text-lg font-medium">
                      Company team conducts food safety training with community
                      members in a rural village hall
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* UN SDGs */}
        <section className="container mx-auto px-4 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-7xl mx-auto"
          >
            <div className="text-center mb-12">
              <FaGlobeAmericas className="text-6xl text-blue-600 mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                {t("about.sdg.title")}
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-4xl mx-auto">
                {t("about.sdg.subtitle")}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {/* SDG 1 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-shadow"
              >
                <div className="bg-red-600 text-white rounded-xl p-4 text-center mb-4">
                  <div className="text-4xl font-bold mb-2">1</div>
                  <div className="text-sm font-semibold">
                    {t("about.sdg.sdg1.title")}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  {t("about.sdg.sdg1.heading")}
                </h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                  {t("about.sdg.sdg1.description")}
                </p>
              </motion.div>

              {/* SDG 2 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-shadow"
              >
                <div className="bg-yellow-600 text-white rounded-xl p-4 text-center mb-4">
                  <div className="text-4xl font-bold mb-2">2</div>
                  <div className="text-sm font-semibold">
                    {t("about.sdg.sdg2.title")}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  {t("about.sdg.sdg2.heading")}
                </h3>
                <ul className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed space-y-2">
                  <li className="flex items-start gap-2">
                    <FaCheckCircle className="text-green-600 mt-1 flex-shrink-0" />
                    <span>{t("about.sdg.sdg2.point1")}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FaCheckCircle className="text-green-600 mt-1 flex-shrink-0" />
                    <span>{t("about.sdg.sdg2.point2")}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FaCheckCircle className="text-green-600 mt-1 flex-shrink-0" />
                    <span>{t("about.sdg.sdg2.point3")}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FaCheckCircle className="text-green-600 mt-1 flex-shrink-0" />
                    <span>{t("about.sdg.sdg2.point4")}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FaCheckCircle className="text-green-600 mt-1 flex-shrink-0" />
                    <span>{t("about.sdg.sdg2.point5")}</span>
                  </li>
                </ul>
              </motion.div>

              {/* SDG 5 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-shadow"
              >
                <div className="bg-orange-600 text-white rounded-xl p-4 text-center mb-4">
                  <div className="text-4xl font-bold mb-2">5</div>
                  <div className="text-sm font-semibold">
                    {t("about.sdg.sdg5.title")}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  {t("about.sdg.sdg5.heading")}
                </h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                  {t("about.sdg.sdg5.description")}
                </p>
              </motion.div>

              {/* SDG 7 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-shadow"
              >
                <div className="bg-yellow-500 text-white rounded-xl p-4 text-center mb-4">
                  <div className="text-4xl font-bold mb-2">7</div>
                  <div className="text-sm font-semibold">
                    {t("about.sdg.sdg7.title")}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  {t("about.sdg.sdg7.heading")}
                </h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                  {t("about.sdg.sdg7.description")}
                </p>
              </motion.div>

              {/* SDG 8 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-shadow"
              >
                <div className="bg-red-700 text-white rounded-xl p-4 text-center mb-4">
                  <div className="text-4xl font-bold mb-2">8</div>
                  <div className="text-sm font-semibold">
                    {t("about.sdg.sdg8.title")}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  {t("about.sdg.sdg8.heading")}
                </h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                  {t("about.sdg.sdg8.description")}
                </p>
              </motion.div>

              {/* SDG 12 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
                className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-shadow"
              >
                <div className="bg-amber-600 text-white rounded-xl p-4 text-center mb-4">
                  <div className="text-4xl font-bold mb-2">12</div>
                  <div className="text-sm font-semibold">
                    {t("about.sdg.sdg12.title")}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  {t("about.sdg.sdg12.heading")}
                </h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                  {t("about.sdg.sdg12.description")}
                </p>
              </motion.div>

              {/* SDG 13 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.7 }}
                className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-shadow"
              >
                <div className="bg-green-700 text-white rounded-xl p-4 text-center mb-4">
                  <div className="text-4xl font-bold mb-2">13</div>
                  <div className="text-sm font-semibold">
                    {t("about.sdg.sdg13.title")}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  {t("about.sdg.sdg13.heading")}
                </h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                  {t("about.sdg.sdg13.description")}
                </p>
              </motion.div>

              {/* SDG 15 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.8 }}
                className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-shadow"
              >
                <div className="bg-green-600 text-white rounded-xl p-4 text-center mb-4">
                  <div className="text-4xl font-bold mb-2">15</div>
                  <div className="text-sm font-semibold">
                    {t("about.sdg.sdg15.title")}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  {t("about.sdg.sdg15.heading")}
                </h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                  {t("about.sdg.sdg15.description")}
                </p>
              </motion.div>
            </div>
          </motion.div>
        </section>

        {/* Numbers That Matter */}
        <section className="container mx-auto px-4 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto"
          >
            <div className="text-center mb-12">
              <FaChartLine className="text-6xl text-green-600 mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                {t("about.stats.title")}
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {t("about.stats.subtitle")}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.1, y: -10 }}
                  className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 text-white text-center shadow-xl"
                >
                  <div className="text-4xl mb-3">{stat.icon}</div>
                  <div className="text-3xl font-bold mb-2">{stat.number}</div>
                  <div className="text-sm font-semibold">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* Bottom Spacer */}
        <div className="h-16"></div>
      </div>
    </>
  );
};

export default About;
